import React, { useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Avatar, Box, Drawer, IconButton, Menu, MenuItem, ListItemIcon, Toolbar, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon, LogOut as LogOutIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import ModuleNavBar from './ModuleNavBar';
import ModuleSidebar from './ModuleSidebar';
import type { ModuleId } from '../theme/moduleColors';
import type { ModuleNavigation } from './ModuleSidebar';
import { livestockNavigation } from '../modules/livestock/navigation';
import { wikiNavigation } from '../modules/wiki/navigation';

const DRAWER_WIDTH = 260;
const MODULE_BAR_HEIGHT = 48;

const MODULE_NAV_MAP: Record<ModuleId, ModuleNavigation> = {
  'livestock': livestockNavigation,
  wiki: wikiNavigation,
};

function detectModule(pathname: string): ModuleId | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 2) {
    const twoSeg = `${segments[0]}-${segments[1]}`;
    if (twoSeg in MODULE_NAV_MAP) return twoSeg as ModuleId;
  }
  if (segments.length >= 1 && segments[0] in MODULE_NAV_MAP) return segments[0] as ModuleId;
  return null;
}

// ── Shepherd's Staff SVG (compact, for header) ──
function RaiLogoSmall() {
  return (
    <svg width="22" height="22" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M32 56V20C32 14 36 10 40 10C44 10 46 13 44 16C42 19 38 20 36 20"
        stroke="#FFFFFF"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="28" r="3" fill="#F59E0B" />
      <circle cx="40" cy="13" r="2.5" fill="#F59E0B" />
    </svg>
  );
}

const PortalShell: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const activeModule = useMemo(() => detectModule(location.pathname), [location.pathname]);
  const moduleNav = activeModule ? MODULE_NAV_MAP[activeModule] : null;

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await logout();
    navigate('/login');
  };

  // Orchestrator console takes over full screen — hide portal chrome
  const isOrchestratorFullscreen = location.pathname.includes('/chief/orchestrator');

  const showSidebar = !!moduleNav && !isOrchestratorFullscreen;

  if (isOrchestratorFullscreen) {
    return <Outlet />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: '#1B5E20',
          color: '#FFFFFF',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: '48px !important', px: 2 }}>
          {showSidebar && isMobile && (
            <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1, color: 'rgba(255,255,255,0.8)' }}>
              <MenuIcon size={18} />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RaiLogoSmall />
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#FFFFFF',
                fontFamily: '"IBM Plex Sans Arabic", "IBM Plex Sans", sans-serif',
                letterSpacing: '-0.01em',
              }}
            >
              راعي
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.65)',
                fontFamily: '"IBM Plex Sans", sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                ml: 0.5,
              }}
            >
              Ra'i
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={user?.username ?? 'Account'}>
            <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} size="small">
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.15)', color: '#FFFFFF', fontSize: '0.85rem', border: '1px solid rgba(255,255,255,0.3)' }}>
                {user?.username?.charAt(0).toUpperCase() ?? 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={userMenuAnchor} open={!!userMenuAnchor} onClose={() => setUserMenuAnchor(null)}>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogOutIcon size={16} /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: '48px', position: 'fixed', top: 0, left: 0, right: 0, zIndex: theme.zIndex.drawer }}>
        <ModuleNavBar activeModule={activeModule} />
      </Box>

      <Box sx={{ display: 'flex', mt: `${48 + MODULE_BAR_HEIGHT}px`, minHeight: `calc(100vh - ${48 + MODULE_BAR_HEIGHT}px)` }}>
        {showSidebar && (
          isMobile ? (
            <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
              sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, mt: `${48 + MODULE_BAR_HEIGHT}px`, bgcolor: '#FFFFFF', borderRight: '1px solid #E5E7EB' } }}>
              <ModuleSidebar moduleId={activeModule!} navigation={moduleNav!} />
            </Drawer>
          ) : (
            <Box sx={{ width: DRAWER_WIDTH, flexShrink: 0, borderRight: '1px solid', borderColor: '#E5E7EB', bgcolor: '#FFFFFF', overflowY: 'auto', height: `calc(100vh - ${48 + MODULE_BAR_HEIGHT}px)`, position: 'sticky', top: `${48 + MODULE_BAR_HEIGHT}px` }}>
              <ModuleSidebar moduleId={activeModule!} navigation={moduleNav!} />
            </Box>
          )
        )}
        <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: '#FAFAF5', minHeight: '100%', overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default PortalShell;
